const RECAPTCHA_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
const RECAPTCHA_SCRIPT_ATTRIBUTE = 'data-zeelool-recaptcha';
const RECAPTCHA_LOAD_TIMEOUT_MS = 10_000;
const RECAPTCHA_READY_POLL_MS = 50;

let recaptchaLoadPromise: Promise<void> | null = null;

function isReCaptchaReady(): boolean {
  return typeof window !== 'undefined' && typeof window.grecaptcha?.render === 'function';
}

function findExistingReCaptchaScript(): HTMLScriptElement | null {
  return document.querySelector<HTMLScriptElement>(
    `script[${RECAPTCHA_SCRIPT_ATTRIBUTE}="true"], script[src^="https://www.google.com/recaptcha/api.js"]`,
  );
}

/**
 * 确保 Google reCAPTCHA 显式渲染脚本真正可用。
 * 失败状态不会永久缓存，后续用户操作可以重新加载。
 */
export function ensureReCaptchaLoaded(): Promise<void> {
  if (isReCaptchaReady()) return Promise.resolve();
  if (recaptchaLoadPromise) return recaptchaLoadPromise;

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA is only available in the browser'));
  }

  recaptchaLoadPromise = new Promise<void>((resolve, reject) => {
    const script =
      findExistingReCaptchaScript() ??
      Object.assign(document.createElement('script'), {
        type: 'text/javascript',
        async: true,
        src: RECAPTCHA_SCRIPT_SRC,
      });
    script.setAttribute(RECAPTCHA_SCRIPT_ATTRIBUTE, 'true');

    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearInterval(readyTimer);
      window.clearTimeout(timeoutTimer);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);

      if (error) {
        recaptchaLoadPromise = null;
        script.remove();
        reject(error);
        return;
      }

      recaptchaLoadPromise = null;
      resolve();
    };
    const checkReady = () => {
      if (isReCaptchaReady()) finish();
    };
    const handleLoad = () => checkReady();
    const handleError = () => finish(new Error('Failed to load reCAPTCHA script'));
    const readyTimer = window.setInterval(checkReady, RECAPTCHA_READY_POLL_MS);
    const timeoutTimer = window.setTimeout(
      () => finish(new Error('Timed out loading reCAPTCHA script')),
      RECAPTCHA_LOAD_TIMEOUT_MS,
    );

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    if (!script.isConnected) document.head.appendChild(script);
    checkReady();
  });

  return recaptchaLoadPromise;
}
